import React, { useRef, useState, useEffect } from 'react';
import { Box, IconButton, Tooltip, keyframes } from '@mui/material';
import DeleteSweepRoundedIcon from '@mui/icons-material/DeleteSweepRounded';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import { Geom, geom, xOf, wOf } from './ganttGeometry';
import { packRows } from './ganttPacking';
import { Analysis } from './ganttAnalyze';
import { GanttEntry, GanttLane, GanttFamily } from './ganttTypes';
import { InsertPreview } from './ganttInsert';
import { DowntimeInterval } from '../productionUtils';
import { DowntimeWindow } from '../productionTypes';
import {
  DENSITY, Density, SEPS_VISIBLE_HOUR_PX, DENSE_LABEL_HOUR_PX, AXIS_HEIGHT, RAIL_WIDTH, RUNNING_RAIL_MIN_H, PARK_LANE_H, TOKENS,
  GAP_ANIM_MS, GAP_STAGGER_MS, GAP_STAGGER_MAX_MS, SHIFT_START_MIN, SHIFT_END_MIN,
  FROZEN_ZONE_ENABLED, FREEZE, ZONE_RIBBON_H,
  InkFilter, hasInk,
} from './ganttConfig';
import { frozenZones } from './ganttFrozenZone';
import AcUnitRoundedIcon from '@mui/icons-material/AcUnitRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import GanttBar, { fmtClock } from './GanttBar';
import ParkedChip from './ParkedChip';

// Breathing outline on the held slot — reads as "working on it", not as a
// finished bar, for the second or so the write takes.
const PLACING_PULSE = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.62; }
`;

export interface DragState {
  tile: GanttFamily | null;   // a backlog/parked family being scheduled
  entry?: GanttEntry | null;  // an already-placed bar being moved/reordered
  overLane: string | null;
  dropAbs: number | null;
  /**
   * Where the drop actually lands, recomputed on each pointermove by the lane
   * under the cursor. The drag commit reads this instead of re-deriving a
   * sequence from the raw cursor time, so what you saw is exactly what you get.
   */
  preview?: InsertPreview | null;
}

interface Props {
  hourPx: number;
  windowMin: number;
  base: Date;
  nowAbs: number;
  entries: GanttEntry[];
  lanes: GanttLane[];
  downtime: DowntimeInterval[];
  parkedCount: number;
  parkedFamilies: GanttFamily[];
  selectedId: number | null;
  density?: Density;
  analysis: Analysis;
  drag: DragState | null;
  /**
   * A committed-but-unconfirmed drop. Keeps the gap open and shows a solid
   * ghost in the slot for the duration of the round trip, so releasing doesn't
   * snap the board back to the pre-drag layout before the new data arrives.
   */
  placing?: { press: string; preview: InsertPreview; jobs: string[] } | null;
  onSelect: (id: number | null) => void;
  onHover: (e: GanttEntry, ev: React.MouseEvent) => void;
  onLeave: () => void;
  onGrabFamily: (fam: GanttFamily, ev: React.PointerEvent) => void;
  // Click (not drag) a Parking chip → the held-family menu.
  onParkedChipClick: (fam: GanttFamily, anchor: HTMLElement) => void;
  onBarMenu: (e: GanttEntry, el: HTMLElement) => void;
  onBarGrab: (e: GanttEntry, ev: React.PointerEvent) => void;
  onBlockClick: (w: DowntimeWindow, el: HTMLElement) => void;
  /** Active ink colour filter — bars/chips not carrying the ink fade back. */
  inkFilter?: InkFilter | null;
  /** Admin clean-up: wipe all not-started jobs on a press. Omit to hide. */
  onClearFuture?: (pressCode: string) => void;
  /** Open the printable Pre-Press run sheet for a press. Omit to hide. */
  onPrintRunSheet?: (pressCode: string) => void;
  scrollRef: React.RefObject<HTMLDivElement>;
}

function dayLabel(base: Date, offset: number): string {
  const d = new Date(base);
  d.setDate(d.getDate() + offset);
  return d.toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short' });
}

// Coarse "x ago" for the running-job actuals freshness (mirrors the Prism sync
// chip in ReadyToRunDock). Signals how old the last press upload for this job is.
function relAge(iso: string | null): string {
  if (!iso) return '';
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
// Past this, the running-job data is likely behind (sync pushes every few min).
const ACTUALS_STALE_MIN = 15;

export default function GanttBoard({
  hourPx, windowMin, base, nowAbs, entries, lanes, downtime, parkedCount, parkedFamilies,
  selectedId, density = 'airy', analysis, drag, placing, onSelect, onHover, onLeave, onGrabFamily, onParkedChipClick, onBarMenu,
  onBarGrab, onBlockClick, inkFilter, onClearFuture, onPrintRunSheet, scrollRef,
}: Props) {
  // Re-render once a minute so the running-job "x ago" freshness keeps ticking
  // even when the board is idle (the timestamp itself is fixed and real).
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  const railLanesRef = useRef<HTMLDivElement>(null);
  const onScrollSync = (ev: React.UIEvent<HTMLDivElement>) => {
    if (railLanesRef.current) {
      railLanesRef.current.style.transform = `translateY(${-ev.currentTarget.scrollTop}px)`;
    }
  };

  // ── grab-to-pan: drag the timeline background like a PDF hand tool ─────────
  // Skipped when the press starts on a bar / block / parked chip (data-no-pan)
  // so their own drag still works. `panned` suppresses the click-deselect that
  // would otherwise fire at the end of a pan gesture.
  const pannedRef = useRef(false);
  const onPanDown = (ev: React.PointerEvent) => {
    const el = scrollRef.current;
    if (!el || ev.button !== 0) return;
    if ((ev.target as HTMLElement).closest('[data-no-pan]')) return;
    pannedRef.current = false;
    const sx = ev.clientX;
    const sy = ev.clientY;
    const sLeft = el.scrollLeft;
    const sTop = el.scrollTop;
    el.style.cursor = 'grabbing';
    const move = (m: PointerEvent) => {
      const dx = m.clientX - sx;
      const dy = m.clientY - sy;
      if (!pannedRef.current && Math.hypot(dx, dy) > 4) pannedRef.current = true;
      el.scrollLeft = sLeft - dx;
      el.scrollTop = sTop - dy;
    };
    const up = () => {
      el.style.cursor = '';
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up, { once: true });
  };

  const g: Geom = geom(hourPx, windowMin);
  const contentW = wOf(g.window, g);
  const dm = DENSITY[density];
  const winStart = g.anchor;
  const winEnd = g.anchor + g.window;
  const inWindow = (e: GanttEntry) => e.absEnd > winStart && e.absStart < winEnd;
  const baseMs = base.getTime();
  const dtMin = (d: Date) => (d.getTime() - baseMs) / 60000;

  // per-lane sub-row packing → drives BOTH rail + track heights so they align
  const laneData = lanes.map((lane) => {
    const list = entries.filter((e) => e.press === lane.code && inWindow(e));
    const packed = packRows(list);
    // A running press shows extra rail rows (machine status, speed/operator,
    // sheet progress); reserve height so they don't overrun the lane below.
    // Deliberately NOT scoped to day 0: a long run that started yesterday is
    // still what the press is doing now (see the rail's `running` lookup).
    const isRunning = entries.some((e) => e.press === lane.code && e.status === 'in_progress');
    const h = lane.kind === 'park'
      ? PARK_LANE_H
      : Math.max(
        dm.rowH + 2 * dm.padV,
        packed.rows * dm.rowH + (packed.rows - 1) * dm.gap + 2 * dm.padV,
        isRunning ? RUNNING_RAIL_MIN_H : 0,
      );
    return { lane, packed, h };
  });
  const topFor = (row: number) => dm.padV + row * (dm.rowH + dm.gap);

  // hour ticks (only whole hours)
  const hours: number[] = [];
  for (let h = 0; h * 60 <= g.window; h += g.hourStep) {
    const absH = g.anchor + h * 60;
    if (absH % 60 === 0) hours.push(absH);
  }
  // day caps
  const dayCaps: { d: number; x: number }[] = [];
  const firstDay = Math.floor(g.anchor / 1440);
  const lastDay = Math.floor((g.anchor + g.window) / 1440);
  for (let d = firstDay; d <= lastDay; d++) {
    const capAbs = Math.max(d * 1440, g.anchor);
    if (capAbs < g.anchor + g.window) dayCaps.push({ d, x: xOf(capAbs, g) });
  }
  // off-shift bands (18:00–06:00) — subtle shading across the whole canvas
  const nights: { left: number; width: number }[] = [];
  for (let d = firstDay; d <= lastDay + 1; d++) {
    const s = d * 1440 + SHIFT_END_MIN;
    const en = (d + 1) * 1440 + SHIFT_START_MIN;
    const l = Math.max(xOf(s, g), 0);
    const r = Math.min(xOf(en, g), contentW);
    if (r > l) nights.push({ left: l, width: r - l });
  }
  const nowX = xOf(nowAbs, g);
  const showNow = nowAbs >= g.anchor && nowAbs <= g.anchor + g.window;
  const showSeps = g.hourPx >= SEPS_VISIBLE_HOUR_PX;

  // Frozen-zone overlay — the "prep runs a day ahead" rhythm, drawn on a
  // rolling basis. Visual only: nothing here blocks a drag or a drop yet.
  // See the FROZEN ZONE block in ganttConfig.ts and ganttFrozenZone.ts.
  const fz = FROZEN_ZONE_ENABLED ? frozenZones(nowAbs) : null;
  const clampX = (abs: number) => Math.min(Math.max(xOf(abs, g), 0), contentW);
  const wdayAt = (absMin: number) => {
    const d = new Date(base);
    d.setDate(d.getDate() + Math.floor(absMin / 1440));
    return d.toLocaleDateString('en-NZ', { weekday: 'short' });
  };
  const zoneSegs = fz ? [
    {
      key: 'locked', l: clampX(fz.lockedStart), r: clampX(fz.prepStart),
      wash: FREEZE.lockedWash, icon: 'snow' as const, label: 'LOCKED · ON PRESS',
      tip: `Today's production day (${wdayAt(fz.lockedStart)} 06:00 → ${wdayAt(fz.prepStart)} 06:00). `
        + 'Its ink kits and CtP plates were prepped yesterday and are staged at the presses.',
    },
    {
      key: 'prep', l: clampX(fz.prepStart), r: clampX(fz.freezeAbs),
      wash: FREEZE.prepWash, icon: 'snow' as const, label: 'FROZEN · PREP RUNNING — INK KITS + CTP PLATES',
      tip: `Froze at 06:00 this morning: ink kitting and plate imaging are running now for everything `
        + `scheduled ${wdayAt(fz.prepStart)} 06:00 → ${wdayAt(fz.freezeAbs)} 06:00. `
        + 'Moving a job in this window strands kitted ink and imaged plates.',
    },
    {
      // Next in line: the first day past the horizon. Not frozen yet, but its
      // countdown is running — that's the day the next 06:00 commits.
      key: 'next', l: clampX(fz.freezeAbs), r: clampX(fz.freezeAbs + 1440),
      wash: FREEZE.nextWash, icon: 'clock' as const,
      label: `PREP KICKS OFF ${wdayAt(fz.nextFreezeAt).toUpperCase()} 06:00`,
      tip: `Next to freeze: at 06:00 ${wdayAt(fz.nextFreezeAt)} morning, ink kitting and plate imaging `
        + `kick off for everything scheduled ${wdayAt(fz.freezeAbs)} 06:00 → ${wdayAt(fz.freezeAbs + 1440)} 06:00. `
        + 'Get this day into its final shape before then.',
    },
    {
      key: 'open', l: clampX(fz.freezeAbs + 1440), r: contentW,
      wash: null, icon: null, label: 'OPEN · PLAN FREELY',
      tip: 'Beyond the prep pipeline — reschedule freely. Each day rolls through '
        + '"prep kicks off" → frozen → on press as the 06:00 boundaries pass.',
    },
  ] : [];

  return (
    <Box sx={{ display: 'flex', flex: 1, minHeight: 0, minWidth: 0, background: TOKENS.panel }}>
      {/* fixed lane rail */}
      <Box sx={{ position: 'relative', width: RAIL_WIDTH, flexShrink: 0, overflow: 'hidden', borderRight: `1px solid ${TOKENS.line}` }}>
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, height: AXIS_HEIGHT, zIndex: 3,
          display: 'flex', alignItems: 'flex-end', px: 2, pb: 1,
          background: TOKENS.panel, borderBottom: `1px solid ${TOKENS.line}`,
          fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', color: TOKENS.ink3,
        }}>PRESS</Box>
        <Box ref={railLanesRef} sx={{ position: 'absolute', top: AXIS_HEIGHT, left: 0, right: 0, willChange: 'transform' }}>
          {laneData.map(({ lane, h }) => {
            // "What is this press doing right now" — an in_progress entry on the
            // lane, whenever it started. This used to be scoped to day 0, which
            // made a lane read Idle whenever the current run began the previous
            // day: P15 on 2026-07-28 sat at "Idle" while the press was reporting
            // Running at 14,000/hr on a job that started 15:58 the day before.
            const running = entries.find((e) => e.press === lane.code && e.status === 'in_progress');
            const isDrop = drag?.overLane === lane.code;
            const dropSx = isDrop ? { background: `${TOKENS.accent}14` } : null;
            if (lane.kind === 'park') {
              return (
                <Box key={lane.code} sx={{ height: h, px: 2, py: 1.75, borderBottom: `1px solid ${TOKENS.lineSoft}`, ...dropSx }}>
                  <Box sx={{ fontSize: 15, fontWeight: 700, color: TOKENS.ink }}>Parking</Box>
                  <Box sx={{ fontSize: 11, color: TOKENS.ink3, mt: 0.5 }}>{parkedCount ? `${parkedCount} parked` : 'Drop to hold'}</Box>
                </Box>
              );
            }
            return (
              <Box key={lane.code} sx={{ height: h, px: 2, py: 1.75, borderBottom: `1px solid ${TOKENS.lineSoft}`, ...dropSx }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Box sx={{ fontSize: 15, fontWeight: 700, color: TOKENS.ink, letterSpacing: '-0.01em', flex: 1, minWidth: 0 }}>{lane.code}</Box>
                  {onPrintRunSheet && (
                    <Tooltip title="Print run sheet — Pre-Press plate &amp; material prep" placement="top" arrow>
                      <IconButton
                        size="small" data-no-pan
                        aria-label={`Print run sheet for ${lane.code}`}
                        onClick={(ev) => { ev.stopPropagation(); onPrintRunSheet(lane.code); }}
                        sx={{
                          p: 0.25, color: TOKENS.ink3, alignSelf: 'center',
                          '&:hover': { color: TOKENS.accent, background: `${TOKENS.accent}14` },
                        }}
                      >
                        <PrintRoundedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {onClearFuture && (
                    <Tooltip title="Clear plan — delete all not-started jobs (admin)" placement="top" arrow>
                      <IconButton
                        size="small" data-no-pan
                        onClick={(ev) => { ev.stopPropagation(); onClearFuture(lane.code); }}
                        sx={{
                          p: 0.25, color: TOKENS.ink3, alignSelf: 'center',
                          '&:hover': { color: TOKENS.bad, background: 'rgba(220,38,38,0.08)' },
                        }}
                      >
                        <DeleteSweepRoundedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, fontSize: 11, fontWeight: 600, color: running ? (running.machineStatus === 'Idle' ? TOKENS.warn : TOKENS.good) : TOKENS.ink3 }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: running ? (running.machineStatus === 'Idle' ? TOKENS.warn : TOKENS.good) : TOKENS.ink3 }} />
                  {running ? `${running.machineStatus ?? 'Running'} ${running.job}` : 'Idle'}
                  {running && running.actualUpdatedAt && (() => {
                    const ageMin = Math.floor((Date.now() - new Date(running.actualUpdatedAt).getTime()) / 60000);
                    const stale = ageMin >= ACTUALS_STALE_MIN;
                    return (
                      <Tooltip title={`Press actuals last updated ${new Date(running.actualUpdatedAt).toLocaleString()}${stale ? ' · may be behind' : ''}`} arrow>
                        <Box component="span" sx={{ fontWeight: 500, color: stale ? TOKENS.warn : TOKENS.ink3 }}>
                          · {relAge(running.actualUpdatedAt)}
                        </Box>
                      </Tooltip>
                    );
                  })()}
                </Box>
                {running && (running.machineActivity || (running.liveSpeed != null && running.liveSpeed > 0) || running.operator) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, fontSize: 10.5, color: TOKENS.ink3, flexWrap: 'wrap' }}>
                    {running.machineActivity && <Box component="span" sx={{ fontWeight: 600 }}>{running.machineActivity}</Box>}
                    {running.liveSpeed != null && running.liveSpeed > 0 && (
                      <Box component="span" sx={{ fontFamily: TOKENS.monoFont }}>· {running.liveSpeed.toLocaleString()}/hr</Box>
                    )}
                    {running.operator && <Box component="span">· {running.operator}</Box>}
                  </Box>
                )}
                {running && running.sheetsDone != null && running.sheets > 0 && (
                  <Box sx={{ mt: 0.25, fontSize: 10.5, fontWeight: 600, color: TOKENS.ink2, fontFamily: TOKENS.monoFont, fontVariantNumeric: 'tabular-nums' }}>
                    {running.sheetsDone.toLocaleString()} / {running.sheets.toLocaleString()} sheets · {Math.round((running.progress ?? 0) * 100)}%
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* scrollable timeline */}
      <Box
        ref={scrollRef} onScroll={onScrollSync} onPointerDown={onPanDown}
        onClick={() => { if (pannedRef.current) { pannedRef.current = false; return; } onSelect(null); }}
        sx={{ flex: 1, minWidth: 0, overflow: 'auto', position: 'relative', cursor: 'grab' }}
      >
        <Box sx={{ width: contentW, position: 'relative' }}>
          {/* axis */}
          <Box sx={{
            position: 'sticky', top: 0, height: AXIS_HEIGHT, zIndex: 5,
            background: TOKENS.panel, borderBottom: `1px solid ${TOKENS.line}`,
          }}>
            {dayCaps.map((c) => (
              <Box key={c.d} sx={{
                position: 'absolute', left: c.x + 6, top: 8, fontSize: 11, fontWeight: 600,
                color: c.d === 0 ? TOKENS.accent : TOKENS.ink2, display: 'flex', alignItems: 'center', gap: 0.5,
              }}>
                {c.d === 0 && <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: TOKENS.accent }} />}
                {c.d === 0 ? 'Today' : dayLabel(base, c.d)}
              </Box>
            ))}
            {hours.map((absH) => {
              const hod = (absH / 60) % 24;
              if (hod === 0) return null; // midnight is covered by the day cap
              const shift = hod === 6 || hod === 18;
              const lab = g.hourPx >= DENSE_LABEL_HOUR_PX
                ? fmtClock(absH).replace(':00', '')
                : `${((hod + 11) % 12) + 1}${hod < 12 ? 'a' : 'p'}`;
              return (
                <Box key={absH} sx={{
                  position: 'absolute', left: xOf(absH, g), top: 32, fontSize: 10,
                  fontFamily: TOKENS.monoFont, color: shift ? TOKENS.ink2 : TOKENS.ink3,
                  fontWeight: shift ? 700 : 400, fontVariantNumeric: 'tabular-nums',
                }}>{lab}</Box>
              );
            })}
            {showNow && (
              <Box sx={{ position: 'absolute', left: nowX - 16, top: 4, zIndex: 6 }}>
                <Box sx={{ background: TOKENS.accent, color: '#fff', fontSize: 9, fontWeight: 700, px: 0.5, borderRadius: '4px', textAlign: 'center' }}>NOW</Box>
              </Box>
            )}
            {/* zone ribbon — the rolling locked / frozen / open bands, labelled.
                Hover any band for the full story; the washes repeat down the
                lanes so the zones read across the whole board. */}
            {fz && (
              <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: ZONE_RIBBON_H, borderTop: `1px solid ${TOKENS.lineSoft}` }}>
                {zoneSegs.map((z) => (z.r > z.l && (
                  <Tooltip key={z.key} title={z.tip} arrow placement="bottom">
                    <Box sx={{
                      position: 'absolute', top: 0, bottom: 0, left: z.l, width: z.r - z.l,
                      background: z.wash ?? 'transparent',
                      display: 'flex', alignItems: 'center', gap: 0.5, pl: 0.75,
                      overflow: 'hidden', whiteSpace: 'nowrap', cursor: 'help',
                      fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                      color: z.icon ? FREEZE.ink : TOKENS.ink3,
                    }}>
                      {z.icon === 'snow' && <AcUnitRoundedIcon sx={{ fontSize: 11, flexShrink: 0 }} />}
                      {z.icon === 'clock' && <ScheduleRoundedIcon sx={{ fontSize: 11, flexShrink: 0 }} />}
                      {z.label}
                    </Box>
                  </Tooltip>
                )))}
                {/* the horizon itself, restated where the bands meet */}
                <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: clampX(fz.freezeAbs) - 1, borderLeft: `2px dashed ${FREEZE.ink}` }} />
              </Box>
            )}
            {/* freeze-horizon flag — pinned to the line, above the ribbon */}
            {fz && (
              <Tooltip
                arrow placement="bottom"
                title={`Everything left of this line is frozen — its prep has already been kicked off. `
                  + `Rolls forward one day at 06:00 ${wdayAt(fz.nextFreezeAt)} morning, `
                  + `when ${wdayAt(fz.freezeAbs)} 06:00 → ${wdayAt(fz.freezeAbs + 1440)} 06:00 goes into prep.`}
              >
                <Box sx={{
                  position: 'absolute', left: clampX(fz.freezeAbs) + 4, top: 30, zIndex: 6,
                  display: 'flex', alignItems: 'center', gap: 0.4, cursor: 'help',
                  background: FREEZE.ink, color: '#fff', fontSize: 9, fontWeight: 700,
                  px: 0.5, py: '1px', borderRadius: '4px', whiteSpace: 'nowrap',
                }}>
                  <AcUnitRoundedIcon sx={{ fontSize: 10 }} />
                  FREEZE HORIZON
                </Box>
              </Tooltip>
            )}
          </Box>

          {/* lanes */}
          {laneData.map(({ lane, packed, h }) => {
            const downs = downtime.filter((d) => d.source.press_code === lane.code);
            const isDrop = drag?.overLane === lane.code;
            // The gap belongs to the lane under the cursor while dragging, and
            // to the target lane afterwards until the drop is confirmed.
            const isPlacing = !drag && placing?.press === lane.code && lane.kind !== 'park';
            const preview = isDrop && lane.kind !== 'park'
              ? drag?.preview ?? null
              : isPlacing ? placing!.preview : null;
            const pushedIds = new Set(preview?.pushedIds ?? []);
            const shiftPx = preview ? wOf(preview.shiftMin, g) : 0;
            // Order the push so the nearest bar moves first — the displacement
            // then reads as a wave travelling down the lane rather than a jump.
            const pushOrder = new Map((preview?.pushedIds ?? []).map((id, k) => [id, k]));
            return (
              <Box key={lane.code} data-lane={lane.code} data-lane-kind={lane.kind} sx={{
                position: 'relative', height: h, borderBottom: `1px solid ${TOKENS.lineSoft}`,
                background: lane.kind === 'park' ? TOKENS.bg : 'transparent',
                ...(isDrop ? { boxShadow: `inset 0 0 0 2px ${TOKENS.accent}55` } : null),
              }}>
                {/* gridlines + off-shift bands */}
                <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                  {nights.map((n, i) => (
                    <Box key={`n${i}`} sx={{ position: 'absolute', top: 0, bottom: 0, left: n.left, width: n.width, background: 'rgba(38,32,27,0.035)' }} />
                  ))}
                  {/* frozen-zone washes — same spans as the axis ribbon, so the
                      locked/frozen days read as vertical bands down the board */}
                  {zoneSegs.filter((z) => z.wash && z.r > z.l).map((z) => (
                    <Box key={`fz${z.key}`} sx={{ position: 'absolute', top: 0, bottom: 0, left: z.l, width: z.r - z.l, background: z.wash! }} />
                  ))}
                  {/* seam between "on press" and "in prep" — quiet, under the bars */}
                  {fz && (
                    <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: xOf(fz.prepStart, g) - 1, borderLeft: `1px dashed ${FREEZE.ink}55` }} />
                  )}
                  {hours.map((absH) => {
                    const hod = (absH / 60) % 24;
                    const strong = hod === 0 || hod === 6 || hod === 18;
                    return <Box key={absH} sx={{ position: 'absolute', top: 0, bottom: 0, left: xOf(absH, g), width: '1px', background: strong ? TOKENS.line : TOKENS.lineSoft }} />;
                  })}
                </Box>

                {/* downtime bands — all clickable: one-off blocks to delete,
                    recurring closures (e.g. the weekend band) to edit/delete. */}
                {downs.map((d, i) => {
                  const l = xOf(dtMin(d.start), g);
                  const r = xOf(dtMin(d.end), g);
                  if (r < 0 || l > contentW) return null;
                  const w = Math.max(r - l, 4);
                  const editable = true;
                  // Closed-band livery. Standing recurring closures — the
                  // weekend / plant-closed band — stay red. One-off blocks
                  // (maintenance) are blue, so "the plant is shut" and "this
                  // press is out" read apart at a glance instead of looking
                  // like the same event.
                  const plantClosure = d.source.kind === 'recurring';
                  const stripe = plantClosure ? 'rgba(220,38,38,0.55)' : 'rgba(37,99,235,0.55)';
                  const stripeInk = plantClosure ? TOKENS.bad : '#2563eb';
                  return (
                    <Box
                      key={`dt${i}`} title={d.source.label ?? 'Downtime'}
                      {...(editable ? { 'data-no-pan': '1' } : {})}
                      onClick={editable ? (ev) => { ev.stopPropagation(); onBlockClick(d.source, ev.currentTarget); } : undefined}
                      sx={{
                        position: 'absolute', left: l, width: w, top: dm.padV, bottom: dm.padV, borderRadius: '6px',
                        // Hazard stripe on EVERY closed band, so "press is not
                        // available" reads the same whatever put the band there
                        // — only the colour distinguishes why (see above).
                        background: `repeating-linear-gradient(45deg, #ffffff 0 6px, ${stripe} 6px 12px)`,
                        border: `1px solid ${stripe}`,
                        display: 'flex', alignItems: 'center', px: 0.5, overflow: 'hidden', zIndex: 1,
                        cursor: editable ? 'pointer' : 'default',
                        '&:hover': editable ? { borderColor: TOKENS.accent, boxShadow: `inset 0 0 0 1px ${TOKENS.accent}55` } : undefined,
                      }}
                    >
                      {w > 60 && (
                        <Box sx={{
                          fontSize: 10, whiteSpace: 'nowrap',
                          // The stripe is busy — a chip keeps the label readable.
                          color: stripeInk, fontWeight: 700, background: 'rgba(255,255,255,0.92)',
                          borderRadius: '4px', px: 0.5, py: '1px',
                        }}>{d.source.label ?? 'Downtime'}</Box>
                      )}
                    </Box>
                  );
                })}

                {lane.kind === 'park' && (
                  // Held jobs as draggable chips, pinned to the left so they
                  // stay visible regardless of timeline scroll. Drag one up
                  // onto a press to schedule it.
                  <Box sx={{
                    position: 'sticky', left: 0, top: 0, height: h, width: 'fit-content', maxWidth: '100%',
                    display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start', gap: 0.75,
                    px: 1.25, py: 1, overflowY: 'auto', zIndex: 2,
                  }}>
                    {parkedFamilies.length === 0 ? (
                      <Box sx={{ fontSize: 12, color: TOKENS.ink3, alignSelf: 'center' }}>Drop a job here to hold it</Box>
                    ) : (
                      parkedFamilies.map((fam) => (
                        <ParkedChip
                          key={fam.famId} fam={fam}
                          dragging={drag?.tile?.famId === fam.famId}
                          onGrab={(ev) => onGrabFamily(fam, ev)}
                          onClickChip={(anchor) => onParkedChipClick(fam, anchor)}
                        />
                      ))
                    )}
                  </Box>
                )}

                {lane.kind !== 'park' && packed.items.length === 0 && (
                  <Box sx={{ position: 'absolute', left: 12, top: dm.padV + 8, fontSize: 11, color: TOKENS.ink3 }}>No runs scheduled</Box>
                )}

                {packed.items.map(({ e, row }) => (
                  <GanttBar
                    key={e.id}
                    e={e} g={g} top={topFor(row)} hPx={dm.rowH}
                    selected={selectedId === e.id} showSeps={showSeps}
                    dragging={drag?.entry?.id === e.id}
                    shiftPx={pushedIds.has(e.id) ? shiftPx : 0}
                    shiftDelayMs={Math.min((pushOrder.get(e.id) ?? 0) * GAP_STAGGER_MS, GAP_STAGGER_MAX_MS)}
                    inkDimmed={!!inkFilter && !hasInk(e.seps, inkFilter.key)}
                    flags={analysis.flags[String(e.id)]}
                    onHover={onHover} onLeave={onLeave} onSelect={onSelect} onBarMenu={onBarMenu}
                    onBarGrab={onBarGrab}
                  />
                ))}

                {/* Gap-opening drop preview. The ghost is drawn at the position
                    the job will REALLY occupy (predecessor's end, walked around
                    any closure) — not at the cursor — while the bars behind it
                    slide right to open the gap. Preview == committed result. */}
                {preview && (
                  <>
                    {/* insertion caret at the seam */}
                    <Box sx={{
                      position: 'absolute', left: xOf(preview.startAbs, g) - 1, top: dm.padV - 6,
                      width: 2, height: dm.rowH + 12, background: TOKENS.accent,
                      zIndex: 9, pointerEvents: 'none',
                      '&::before': {
                        content: '""', position: 'absolute', top: -4, left: -3,
                        width: 8, height: 8, borderRadius: '50%', background: TOKENS.accent,
                      },
                    }} />
                    {/* The incoming run, split around closures like a real bar.
                        Dashed while the pointer is still down; once dropped it
                        firms up into a solid "placing" bar that holds the slot
                        until the server's version of the lane arrives. */}
                    {preview.segments.map((s, i) => (
                      <Box key={`ip${i}`} sx={{
                        position: 'absolute', left: xOf(s.startMin, g),
                        width: Math.max(wOf(s.endMin - s.startMin, g), 3),
                        top: dm.padV, height: dm.rowH, borderRadius: `${TOKENS.radiusBar}px`,
                        border: `2px ${isPlacing ? 'solid' : 'dashed'} ${TOKENS.accent}`,
                        background: isPlacing ? `${TOKENS.accent}2e` : `${TOKENS.accent}1a`,
                        zIndex: 8, pointerEvents: 'none',
                        transition: `background ${GAP_ANIM_MS}ms ease-out`,
                        ...(isPlacing ? { animation: `${PLACING_PULSE} 1.1s ease-in-out infinite` } : null),
                      }} />
                    ))}
                    {/* what's about to happen / what just happened, in words */}
                    <Box sx={{
                      position: 'absolute', left: xOf(preview.startAbs, g) + 6, top: dm.padV + 4,
                      zIndex: 10, pointerEvents: 'none', whiteSpace: 'nowrap',
                      background: TOKENS.accent, color: '#fff', borderRadius: '6px',
                      px: 0.75, py: 0.25, fontSize: 10.5, fontWeight: 700,
                      boxShadow: TOKENS.shadowSm,
                    }}>
                      {isPlacing ? (
                        `Placing ${placing!.jobs.length === 1 ? placing!.jobs[0] : `${placing!.jobs.length} jobs`}…`
                      ) : (
                        <>
                          {preview.beforeJob ? `Insert before ${preview.beforeJob}` : 'Add to end'}
                          {' · '}{fmtClock(preview.startAbs)}
                          {preview.pushedIds.length > 0 && ` · ${preview.pushedIds.length} pushed later`}
                        </>
                      )}
                    </Box>
                  </>
                )}

                {/* the freeze horizon crosses every lane, over the bars like
                    NOW — a run straddling it is exactly what should catch the
                    eye (its plates/ink are half-committed) */}
                {fz && lane.kind !== 'park' && (
                  <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: clampX(fz.freezeAbs) - 1, borderLeft: `2px dashed ${FREEZE.ink}`, zIndex: 5, pointerEvents: 'none', opacity: 0.75 }} />
                )}
                {showNow && lane.kind !== 'park' && (
                  <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: nowX, width: '2px', background: TOKENS.accent, zIndex: 6, pointerEvents: 'none' }} />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
